import { useState, useEffect } from 'react';
import { ClipboardCheck, Save, CheckCircle2 } from 'lucide-react';
import { useStore } from '@/store/StoreContext';
import { Card, SectionTitle, TextArea, Label, EmptyState, Button } from '@/components/ui';

export function WeeklyRagView() {
  const { activeClass, activeClassAgents, updateWeeklyEvaluation, userRole } = useStore();

  const [selectedAgentId, setSelectedAgentId] = useState(activeClassAgents[0]?.id || '');
  const [selectedWeek, setSelectedWeek] = useState<number>(1);
  const [ragStatus, setRagStatus] = useState<'Green' | 'Amber' | 'Red'>('Green');
  const [trainerNotes, setTrainerNotes] = useState('');
  const [saved, setSaved] = useState(false);

  const selectedAgent = activeClassAgents.find((a) => a.id === selectedAgentId);

  // Load existing eval when agent or week changes
  useEffect(() => {
    if (selectedAgent) {
      const existing = selectedAgent.weeklyEvaluations.find((e) => e.weekNumber === selectedWeek);
      if (existing && existing.calculatedRAG && existing.calculatedRAG !== 'Not Rated') {
        setRagStatus(existing.calculatedRAG as any);
        setTrainerNotes(existing.notes || '');
      } else {
        setRagStatus('Green');
        setTrainerNotes('');
      }
    }
  }, [selectedAgentId, selectedWeek, activeClassAgents]);

  const handleAgentChange = (agentId: string) => {
    setSelectedAgentId(agentId);
  };

  const handleWeekChange = (week: number) => {
    setSelectedWeek(week);
  };

  const handleSave = () => {
    if (userRole === 'viewer') {
      alert('Viewers have read-only access.');
      return;
    }
    if (!selectedAgentId) return;
    updateWeeklyEvaluation(selectedAgentId, selectedWeek, ragStatus, trainerNotes);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionTitle
        title="Weekly RAG Evaluations"
        subtitle="Evaluate trainee performance and compliance on a weekly basis."
        icon={<ClipboardCheck className="w-6 h-6" />}
      />

      {!activeClass || activeClassAgents.length === 0 ? (
        <Card className="p-12 text-center text-slate-500">
          <EmptyState message="No active class or trainees found. Please add trainees to the roster first." />
        </Card>
      ) : (
        <Card className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <Label>Select Trainee</Label>
              <select
                value={selectedAgentId}
                onChange={(e) => handleAgentChange(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 bg-white focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              >
                {activeClassAgents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name} ({agent.pNumber})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label>Training Week</Label>
              <select
                value={selectedWeek}
                onChange={(e) => handleWeekChange(Number(e.target.value))}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 bg-white focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              >
                <option value={1}>Week 1 Evaluation</option>
                <option value={2}>Week 2 Evaluation</option>
                <option value={3}>Week 3 Evaluation</option>
              </select>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label>RAG Status Rating</Label>
              <div className="flex gap-3">
                {(['Green', 'Amber', 'Red'] as const).map((status) => (
                  <button
                    key={status}
                    type="button"
                    onClick={() => setRagStatus(status)}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-bold border transition-all cursor-pointer ${
                      ragStatus === status
                        ? status === 'Green'
                          ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                          : status === 'Amber'
                          ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                          : 'bg-red-600 text-white border-red-600 shadow-sm'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Trainer Notes & Feedback</Label>
              <TextArea
                value={trainerNotes}
                onChange={setTrainerNotes}
                placeholder="Provide detailed feedback for this week..."
                rows={4}
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
              {saved && (
                <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 animate-fade-in">
                  <CheckCircle2 className="h-4 w-4" /> Saved!
                </span>
              )}
              <Button onClick={handleSave}>
                <Save className="h-4 w-4" /> Save Weekly Evaluation
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
